// LiveMonitorPage.js
import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { FiArrowLeft, FiRefreshCw, FiActivity, FiAlertTriangle, FiClock } from 'react-icons/fi';
import { exams as examsApi } from '../../api';
import io from 'socket.io-client';
import toast from 'react-hot-toast';

export function LiveMonitorPage() {
  const { id: examId } = useParams();
  const [exam, setExam] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const socketRef = useRef(null);

  const load = async () => {
    try {
      const [eRes, cRes] = await Promise.all([examsApi.getOne(examId), examsApi.getMonitoring(examId)]);
      setExam(eRes.data.data);
      setCandidates(cRes.data.data || []);
    } catch { toast.error('Failed to load monitoring data'); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    const token = localStorage.getItem('accessToken');
    const socket = io(process.env.REACT_APP_WS_URL || 'http://localhost:5000', { auth: { token } });
    socketRef.current = socket;
    socket.emit('join_exam_monitor', examId);

    socket.on('candidate_started', (d) => {
      toast.success(`${d.name} started the exam`, { icon: '▶️' });
      load();
    });
    socket.on('candidate_submitted', () => load());
    socket.on('candidate_progress', (d) => {
      setCandidates(prev => prev.map(c => c.candidate_id === d.userId ? { ...c, answered_count: d.answeredCount } : c));
    });

    const interval = setInterval(load, 30000);
    return () => { socket.disconnect(); clearInterval(interval); };
  }, [examId]);

  const activeCount = candidates.length;
  const avgProgress = candidates.length ? Math.round(candidates.reduce((a, c) => a + (parseInt(c.answered_count) || 0), 0) / candidates.length) : 0;
  const flaggedCount = candidates.filter(c => c.violation_count > 0).length;

  return (
    <div className="space-y-5 animate-fade-in">
      <div className="flex items-center gap-3">
        <Link to={`/admin/exams/${examId}`} className="p-2 rounded-lg hover:bg-gray-100 text-gray-500"><FiArrowLeft size={18} /></Link>
        <div className="flex-1">
          <h1 className="text-xl font-black text-gray-900">Live Monitor — {exam?.title}</h1>
          <p className="text-gray-500 text-sm flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse inline-block" /> Real-time monitoring</p>
        </div>
        <button onClick={load} className="btn-secondary text-sm py-2"><FiRefreshCw size={14} /> Refresh</button>
      </div>

      <div className="grid grid-cols-3 gap-4">
        {[
          { icon: FiActivity, label: 'Active Now', value: activeCount, color: 'bg-emerald-500' },
          { icon: FiClock, label: 'Avg Questions Done', value: `${avgProgress}`, color: 'bg-blue-500' },
          { icon: FiAlertTriangle, label: 'Flagged', value: flaggedCount, color: 'bg-amber-500' },
        ].map(({ icon: Icon, label, value, color }) => (
          <div key={label} className="stat-card">
            <div className={`w-10 h-10 rounded-xl ${color} flex items-center justify-center`}><Icon size={18} className="text-white" /></div>
            <div><p className="text-sm text-gray-500">{label}</p><p className="text-2xl font-black text-gray-900">{value}</p></div>
          </div>
        ))}
      </div>

      <div className="card overflow-hidden">
        <div className="p-4 border-b border-gray-100 font-bold text-gray-900">Candidates In Progress</div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead><tr className="border-b border-gray-100">
              <th className="table-th">Candidate</th>
              <th className="table-th">Started</th>
              <th className="table-th">Progress</th>
              <th className="table-th">Time Elapsed</th>
              <th className="table-th">Violations</th>
            </tr></thead>
            <tbody>
              {loading ? [...Array(3)].map((_, i) => <tr key={i}>{[...Array(5)].map((_, j) => <td key={j} className="table-td"><div className="h-4 bg-gray-100 rounded animate-pulse" /></td>)}</tr>)
              : candidates.length === 0 ? <tr><td colSpan={5} className="py-12 text-center text-gray-400">No active candidates</td></tr>
              : candidates.map(c => {
                const elapsed = Math.round(parseFloat(c.elapsed_minutes) || 0);
                const remaining = (exam?.duration_minutes || 60) - elapsed;
                const progress = Math.round(((parseInt(c.answered_count) || 0) / (exam?.total_questions || 1)) * 100);
                return (
                  <tr key={c.id} className={`hover:bg-gray-50/50 ${c.violation_count > 0 ? 'bg-red-50/30' : ''}`}>
                    <td className="table-td">
                      <div><p className="font-semibold text-sm">{c.first_name} {c.last_name}</p><p className="text-xs text-gray-400">{c.email}</p></div>
                    </td>
                    <td className="table-td text-xs text-gray-500">{c.started_at ? new Date(c.started_at).toLocaleTimeString() : '—'}</td>
                    <td className="table-td">
                      <div className="flex items-center gap-2">
                        <div className="w-24 bg-gray-100 rounded-full h-1.5">
                          <div className="h-1.5 bg-navy rounded-full" style={{ width: `${progress}%` }} />
                        </div>
                        <span className="text-xs font-medium">{c.answered_count || 0} done</span>
                      </div>
                    </td>
                    <td className="table-td">
                      <span className={`text-sm font-medium ${remaining < 10 ? 'text-red-600' : 'text-gray-600'}`}>{elapsed} min</span>
                      <span className="text-xs text-gray-400 ml-1">({remaining} left)</span>
                    </td>
                    <td className="table-td">
                      {c.violation_count > 0 ? (
                        <span className="inline-flex items-center gap-1 text-xs font-bold text-red-600 bg-red-50 px-2 py-0.5 rounded-full">
                          <FiAlertTriangle size={10} /> {c.violation_count}
                        </span>
                      ) : <span className="text-xs text-gray-400">None</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default LiveMonitorPage;
